/**
 * Central auth module — single source of truth for token management.
 * All axios instances (old service files + api/client.js) use this.
 */
import axios from "axios";
import env from "../config/env";
import { getOAuthHeaders } from "../constants/oAuthValidation";

// --- Cookie/token helpers ---

export const parseCookies = () => {
  const cookies = {};
  document.cookie.split("; ").forEach((c) => {
    const [name, ...rest] = c.split("=");
    cookies[name] = rest.join("=");
  });
  return cookies;
};

export const getAccessToken = () => {
  const cookies = parseCookies();
  for (const name of Object.keys(cookies)) {
    if (name.includes("accessToken")) {
      return cookies[name];
    }
  }
  return null;
};

// Clears every cookie that feeds getOAuthHeaders()/getAccessToken()'s identity
// checks (accessToken, LastAuthUser, username, Enterprise, userId) — the same
// name patterns those functions match on, so a cleared identity here is a
// cleared identity everywhere the app checks "is there a session". Left
// unmatched: tenant/service-URL cookies (__cw_*), which are CW Browser
// shell-owned and unrelated to the user session.
const AUTH_COOKIE_PATTERNS = ["accessToken", "LastAuthUser", "username", "Enterprise", "userId"];

export const clearAuthCookies = () => {
  const cookies = parseCookies();
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const domain = !isLocalhost && env.domain ? `domain=${env.domain};` : "";
  const secure = isLocalhost ? "" : "secure=true;";
  Object.keys(cookies).forEach((name) => {
    if (AUTH_COOKIE_PATTERNS.some((p) => name.includes(p))) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;${secure}SameSite=Strict;${domain}`;
    }
  });
};

const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
};

export const isTokenExpired = (token) => {
  const decoded = parseJwt(token);
  if (!decoded) return true;
  return decoded.exp * 1000 < Date.now();
};

const setCookie = (name, value, maxAgeSeconds) => {
  let maxAge = maxAgeSeconds;
  if (maxAge == null) {
    const decoded = parseJwt(value);
    maxAge = decoded ? Math.max(decoded.exp - Math.floor(Date.now() / 1000), 0) : 3600;
  }
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const domain = !isLocalhost && env.domain ? `domain=${env.domain};` : "";
  const secure = isLocalhost ? "" : "secure=true;";
  document.cookie = `${name}=${value}; max-age=${maxAge}; path=/; ${secure} SameSite=Strict; ${domain}`;
};

// --- Token refresh (promise-deduplicated) ---

let refreshPromise = null;

// A caller already sitting on /sign-in (typing credentials, or bounced there
// moments ago by another failed call) gets nothing from a second hard
// navigation to the same place — it just interrupts them and refetches every
// asset. Only force the trip when it would actually go somewhere new.
const forceSignIn = () => {
  window.electronAPI?.auth?.logout();
  if (window.location.pathname !== "/sign-in") {
    window.location.assign("/sign-in");
  }
};

const performRefresh = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken || !env.refreshTokenUrl) {
    forceSignIn();
    return null;
  }

  try {
    const res = await axios.post(
      env.refreshTokenUrl,
      { refreshToken },
      { headers: { "Content-Type": "application/json" } }
    );

    if (res.status === 200 && res.data.accessToken) {
      setCookie("accessToken", res.data.accessToken);
      setCookie("Enterprise.accessToken", res.data.accessToken);
      setCookie("Enterprise.refreshToken", res.data.refreshToken);
      // Propagate refreshed token to all webview partitions via authBus
      window.electronAPI?.auth?.tokenRefreshed({
        accessToken: res.data.accessToken,
        refreshToken: res.data.refreshToken,
      });
      return res.data.accessToken;
    }

    forceSignIn();
    return null;
  } catch {
    forceSignIn();
    return null;
  }
};

export const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  // .finally's callback is always scheduled as a microtask, even on a promise
  // that's already settled by the time it's attached — unlike a `finally`
  // block written inside performRefresh itself, which (on the fully
  // synchronous "no refreshToken" path, no await ever hit) would run BEFORE
  // this assignment completes and reset refreshPromise to null one line
  // before it gets overwritten back to a non-null promise. That left the
  // dedup guard permanently stuck "on" after the very first no-token call —
  // every later refresh attempt on the same page, even after a fresh login
  // supplied a real refreshToken, silently short-circuited to the stale
  // cached null instead of ever trying again.
  refreshPromise = performRefresh().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

// --- Interceptors ---

/**
 * Request interceptor: attach current access token to outgoing requests.
 */
const attachAuthToken = (config) => {
  const authHeaders = getOAuthHeaders() || {};
  const { access_token } = authHeaders;
  if (access_token) {
    config.headers.Authorization = /^bearer /i.test(access_token) ? access_token : `Bearer ${access_token}`;
  }
  return config;
};

/**
 * Response interceptor: on 401, refresh token and retry the failed request once.
 * _retried flag prevents infinite retry loops.
 */
const handleAuthError = async (error) => {
  const status = error?.response?.status;
  const msg = error?.message || "";

  if (status === 401 || msg.includes("Unexpected token <") || msg.includes("status code 401")) {
    if (!error.config?._retried) {
      const newToken = await refreshAccessToken();
      if (newToken && error.config) {
        error.config._retried = true;
        error.config.headers.Authorization = `Bearer ${newToken}`;
        return axios(error.config);
      }
    }
  }
  return Promise.reject(error);
};

/**
 * Apply standard auth interceptors to an axios instance.
 * Call this after any tenant baseURL interceptors.
 */
export const applyAuthInterceptors = (instance) => {
  instance.interceptors.request.use(attachAuthToken, (err) => Promise.reject(err));
  instance.interceptors.response.use((res) => res, handleAuthError);
};
